import React, { useEffect } from 'react';

import { useAuth } from '../../../../../auth/Auth';
import { useModal } from '../../../../hooks/modalContext';
import { Offer } from '../../../../utils/types';
import confetti from '../../../../../assets/helpers/modals/rewards/confetti.png';
import { getOffers } from '../../../../../auth/_requests';
import { useNavigate } from 'react-router-dom';

const OffersTab: React.FC = () => {
  const { auth } = useAuth();
  const { setModal } = useModal();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [offers, setOffers] = React.useState<Offer[]>([]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await getOffers(auth?.data.token);
        setOffers(response.slice(0, 4));
      } catch (error) {
        console.error('Error fetching offers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  const formatTitle = (code: string) => {
    switch (code) {
      case 'firstPayment':
        return 'First rent payment';
      case 'firstSubscriptionPayment':
        return 'Recurring payment';
      case 'referralReward':
        return 'Refer & earn';
      case 'thirdPayment':
        return 'Third rent payment';

      case 'threeWeekAdvance':
        return 'Set up recurring payment 3 weeks + 3 business days early';
      case 'twoWeekAdvance':
        return 'Set up recurring payment 2 weeks + 3 business days early';
      case 'oneWeekAdvance':
        return 'Set up recurring payment 1 week + 3 business days early';

      default:
        return code.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    }
  };

  const formatDescription = (code: string) => {
    switch (code) {
      case 'firstPayment':
        return 'Kick things off with a payment of $500 or more and you’re all set!';
      case 'firstSubscriptionPayment':
        return 'Set up automatic credit card payments, no more manual work!';
      case 'referralReward':
        return 'Get a reward when your friend joins TenantPay and makes their first payment.';
      case 'thirdPayment':
        return 'Complete your 3rd rent payment to keep the momentum going!';
      case 'threeWeekAdvance':
        return 'Set up recurring payment 3 weeks + 3 business days early';
      case 'twoWeekAdvance':
        return 'Set up recurring payment 2 weeks + 3 business days early';
      case 'oneWeekAdvance':
        return 'Set up recurring payment 1 week + 3 business days early';

      default:
        return code.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
    }
  };

  const earn = (code: string) => {
    switch (code) {
      case 'referralReward':
        navigate('/refer');
        setModal(null);
        break;
      case 'firstSubscriptionPayment':
        setModal('select-property', {
          paymentType: 'subscription',
        });
        break;
      default:
        setModal('select-property', {
          paymentType: 'direct',
        });
    }
  };
  return (
    <div className='w-full'>
      <div className='w-full'>
        {loading ? (
          <div className='text-gray-500'>Loading offers...</div>
        ) : (
          <div className='w-full flex flex-col gap-6 items-center justify-center '>
            {offers.length > 0 ? (
              offers.map((offer) => (
                <div
                  key={offer.code}
                  className='p-4 min-w-[353px]  border border-[#DEE2EF] rounded-[20px]  hover:shadow-lg transition-shadow cursor-pointer bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)]'
                  onClick={() => earn(offer.code)}
                >
                  <div className='flex flex-col xxs:flex-row gap-5 items-center justify-center w-full'>
                    <div className='relative flex-shrink-0'>
                      {/* Confetti Images */}
                      <div className='absolute -top-3  -left-2 flex gap-1 z-10'>
                        <div
                          className='w-[52px] h-[52px] flex-shrink-0 bg-cover bg-no-repeat'
                          style={{ aspectRatio: '59 / 50', backgroundImage: `url(${confetti})` }}
                        />
                        <div
                          className='w-[52px] h-[52px] flex-shrink-0  bg-cover bg-no-repeat'
                          style={{ aspectRatio: '59 / 50', backgroundImage: `url(${confetti})` }}
                        />
                      </div>

                      {/* Blue Card */}
                      <div className='relative flex-shrink-0 w-[90px] h-[88px] rounded-[28px] bg-[linear-gradient(229.22deg,_#0055FF_-13.57%,_#003BB5_113.63%)] flex flex-col items-center justify-center text-white font-semibold p-12 gap-2'>
                        <span className='text-lg font-medium leading-none'>
                          {Number(offer.maxPoints).toLocaleString('en-US')}
                        </span>
                        <span className='text-sm text-white/90'>pts</span>
                      </div>
                    </div>

                    <div className='flex flex-col gap-2 w-full items-center xxs:items-start'>
                      <span className='text-main text-base font-semibold'>
                        {formatTitle(offer.code)}
                      </span>
                      <span className='text-sm text-light/60 max-w-[50%] text-center xxs:max-w-full xxs:text-start'>
                        {formatDescription(offer.code)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className='text-gray-500'>No offers available</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersTab;
